import Link from 'next/link';
import { URLS } from 'lib/constants';
import classNames from 'classnames';
import {
  BookOpenIcon,
  ChatAlt2Icon,
  DocumentDuplicateIcon,
  StatusOnlineIcon
} from '@heroicons/react/solid';
import { ChevronIcon } from '../../common/icons';
import Hover from './hover';
import Click from './click';
import { analytics } from 'pages';
import { rudderTrack } from '@hooks/rudderTrack';

const { docs, tutorials, apiStatus, support } = URLS;

const resources = [
  {
    name: 'Documentation',
    description:
      'Build your first Clerk app or integrate Clerk with an existing project',
    href: docs,
    icon: BookOpenIcon
  },
  {
    name: 'Tutorials',
    description:
      'Explore our end-to-end tutorials and guides for different application stacks using Clerk',
    href: tutorials,
    icon: DocumentDuplicateIcon
  },
  {
    name: 'Support',
    description: 'Contact us via email, Twitter, or join our Discord',
    href: support,
    icon: ChatAlt2Icon
  },
  {
    name: 'API Status',
    description: `View the current uptime status of Clerk's APIs`,
    href: apiStatus,
    icon: StatusOnlineIcon
  }
];

function ListItem({ item }) {
  const Icon = item.icon;

  if (item.comingSoon) {
    return (
      <li className="flow-root">
        <div>
          <span className="flex px-3 py-2 -mx-3 text-sm font-medium text-gray-500">
            {item.name}
            <span className="ml-2 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-clerk-blue tracking-wide">
              Coming soon
            </span>
          </span>
        </div>
      </li>
    );
  }

  return (
    <li className="flow-root">
      <Link legacyBehavior href={item.href}>
        <a className="flex px-3 py-2 pr-20 -mx-3 text-sm font-medium text-gray-900 transition duration-150 ease-in-out rounded hover:bg-gray-menu-item-hover">
          {Icon && <Icon className="w-5 h-5 mr-2 text-gray-400" />}
          {item.name}
        </a>
      </Link>
    </li>
  );
}

export default function Developers({ textColor }) {
  return (
    <>
      <Hover
        Button={() => (
          <button
            type="button"
            className={classNames(
              textColor,
              'py-4 group items-center text-md font-medium focus:outline-none hidden lg:inline-flex'
            )}
          >
            Developers
          </button>
        )}
        popup={
          <div className="absolute z-10 max-w-md px-2 transform -translate-x-1/2 w-max left-1/2 sm:px-0">
            <div className="overflow-hidden shadow-lg rounded-2xl ring-1 ring-black ring-opacity-5">
              <div className="relative gap-8 px-8 py-6 bg-white p">
                <p className="mb-5 text-lg font-semibold text-black">
                  Developers
                </p>

                <div className="grid grid-cols-1 gap-6">
                  <ul>
                    {resources.map(item => (
                      <li key={item.name} className="flow-root">
                        <Link legacyBehavior href={item.href}>
                          <a className="flex items-start px-3 py-2 -mx-3 transition duration-150 ease-in-out rounded pr-18 hover:bg-gray-menu-item-hover">
                            {item.icon({
                              className: 'flex-shrink-0 w-5 h-5 text-gray-400',
                              'aria-hidden': 'true'
                            })}
                            <div className="ml-2">
                              <p className="mb-1 text-sm font-medium text-gray-900">
                                {item.name}
                              </p>
                              <p className="text-xs font-normal text-gray-500">
                                {item.description}
                              </p>
                            </div>
                          </a>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        }
      />
      <Click
        Button={({ open }) => (
          <button
            type="button"
            className={classNames(
              open ? 'text-gray-900' : 'text-gray-500',
              'w-full group inline-flex justify-between items-center text-base font-medium hover:text-gray-900 focus:outline-none lg:hidden'
            )}
          >
            Developers
            <ChevronIcon
              className={classNames(
                open
                  ? 'fill-current transform rotate-0'
                  : 'fill-current transform -rotate-90',
                'flex-shrink-0 w-6 h-6 ml-2 group-hover:fill-current'
              )}
            />
          </button>
        )}
        popup={
          <div className="w-full px-2 transform sm:px-0">
            <div>
              <div className="grid grid-cols-1 gap-20 sm:grid-cols-2">
                <div className="w-full">
                  <ul className="mt-2">
                    {resources.map(({ icon, name, href }) => (
                      <ListItem
                        key={name}
                        item={{
                          icon,
                          name,
                          href
                        }}
                      />
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
}
